import { Fancybox, Swiper, A11y, Navigation, Pagination, Scrollbar, Autoplay } from 'global-modules';
class SwiperSlider extends HTMLElement {
  constructor() {
    super();

    this.swiper = this.querySelector('[data-swiper]');

    // Stop when the swiper is not found
    if (!this.swiper) return;
  }

  connectedCallback() {
    // Default to improve user experience
    this.swiperOptions = {
      observer: true,
      threshold: 10,
      isRecommendations: false,
      modules: [A11y],
      destroyAfter: false,
      hasVideo: false,
      pauseOnMouseEnter: true,
      slidesPerView: 1,
      hasFancybox: false,
      spaceBetween: 16,
      on: {
        slideChange: function(swiper) {
          if (swiper.isEnd) {
            swiper.el.classList.add('end');
          }
          else {
            swiper.el.classList.remove('end');
          }
        },
      },
    };

    // Check if we have extra options on the HTML
    if (this.dataset.options) {
      const options = JSON.parse(this.dataset.options);
      if (options) {
        this.swiperOptions = {
          ...this.swiperOptions,
          ...options,
        };
      }
    }

    if (this.swiperOptions.hasFancybox) {
      this.fancybox();
    }

    // Load the Navigation, Pagination, Scrollbar, Autoplay modules as needed
    if (this.swiperOptions.pagination) this.swiperOptions.modules.push(Pagination);
    if (this.swiperOptions.navigation) this.swiperOptions.modules.push(Navigation);
    if (this.swiperOptions.scrollbar) this.swiperOptions.modules.push(Scrollbar);
    if (this.swiperOptions.autoplay) this.swiperOptions.modules.push(Autoplay);

    if (this.swiperOptions.destroyAfter) {
      window.addEventListener('resize', debounce(() => {
        this.resizeHandler.bind(this);
      }, 50));

      window.addEventListener('DOMContentLoaded',() => {
        this.resizeHandler.bind(this);
      });
    }
    else {
      // render the options and init the swiper
      this.init();
    }

    window.addEventListener('dialog:open', this.pauseSwiper.bind(this));

    // When the swiper has video
    if (this.swiperOptions.hasVideo) {
      this.debouncedOnLoad = debounce(event => {
        this.swiperObserver(event.type);
      }, 100);

      // check if the swiper is visible
      window.addEventListener('load', this.debouncedOnLoad.bind(this));
      window.addEventListener('scroll', this.debouncedOnLoad.bind(this));

      // when the swiper contains a video, pause it when it's swiped out and play it if it's swiped into view and has autoplay enabled
      this.swiperInstance.on('slideChangeTransitionEnd', swiper => {
        const previousSlide = swiper.slides[swiper.previousIndex];
        const previousSlideType = previousSlide.dataset.swiperVideoType;
        const previousVideo = previousSlide.querySelector(previousSlideType);

        const currentSlide = swiper.slides[swiper.activeIndex];
        const currentSlideType = currentSlide.dataset.swiperVideoType;
        const currentVideo = currentSlide.querySelector(currentSlideType);

        if (previousVideo && !this.getPausedState(previousVideo, previousSlideType)) previousVideo.pauseVideo();
        if (currentVideo && currentVideo.options.autoplay && !this.getPausedState(currentVideo, currentSlideType) && currentVideo.dataset.videoIsPaused !== 'true') currentVideo.playVideo();
      });
    }

    // Listen to extra events when in Shopify editor
    if (Shopify.designMode) {
      // Update the swiper when the section event is triggered
      window.addEventListener('shopify:section:load', event => this.init(event, true));

      // When on block select go to the slide in front-end
      window.addEventListener('shopify:block:select', event => this.handleBlockSelect(event));

      // When on block deselect go to the slide in front-end
      window.addEventListener('shopify:block:deselect', event => this.handleBlockSelect(event));
    }
  }

  disconnectedCallback() {
    window.removeEventListener('dialog:open', this.pauseSwiper.bind(this));

    if (this.swiperOptions.hasVideo) {
      window.removeEventListener('load', this.debouncedOnLoad.bind(this));
      window.removeEventListener('scroll', this.debouncedOnLoad.bind(this));
    }

    // Listen to extra events when in Shopify editor
    if (Shopify.designMode) {
      // Update the swiper when the section event is triggered
      window.removeEventListener('shopify:section:load', event => this.init(event, true));

      // When on block select go to the slide in front-end
      window.removeEventListener('shopify:block:select', event => this.handleBlockSelect(event));

      // When on block deselect go to the slide in front-end
      window.removeEventListener('shopify:block:deselect', event => this.handleBlockSelect(event));
    }

    if (this.swiperOptions.destroyAfter) {
      window.removeEventListener('resize', debounce(() => {
        this.resizeHandler.bind(this);
      }, 50));

      window.removeEventListener('DOMContentLoaded',() => {
        this.resizeHandler.bind(this);
      });
    }
  }

  resizeHandler() {
    const ww = window.innerWidth;
    if (ww >= this.swiperOptions.destroyAfter && this.swiperInstance) {
      this.swiperInstance.destroy();
    }
    else {
      this.init();
    }
  }

  pauseSwiper(event) {
    if (!event?.detail) return;

    if (event.detail.closest('[data-swiper]') === this.swiperInstance.el && this.swiperInstance?.autoplay?.running) {
      this.swiperInstance.autoplay.stop();

      // When the dialog is closed, restart the autoplay
      event.detail.addEventListener('close', () => {
        this.swiperInstance.autoplay.start();
      }, { once: true });
    }
  }

  getPausedState(video, type) {
    switch (type) {
    case 'youtube-video':
      return video.getPausedState();
    case 'vimeo-video':
      return video.paused;
    case 'custom-video':
      return video.videoElement.paused;
    }
  };

  swiperObserver() {
    if (!this.swiperInstance) return;

    // Register IntersectionObserver
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const elementBounds = entry.boundingClientRect;
        const isBottomVisible = !!(elementBounds.bottom < window.innerHeight) && elementBounds.bottom;
        const isTopVisible = !!(elementBounds.top > 0) && elementBounds.top;

        const swiper = this.swiperInstance;
        const currentSlide = swiper.slides[swiper.activeIndex];
        const currentSlideType = currentSlide.dataset.swiperVideoType;
        const currentVideo = currentSlide.querySelector(currentSlideType);

        if (isBottomVisible && isTopVisible || entry.isIntersecting && entry.intersectionRatio > 0.9) {
          if (currentVideo && currentVideo.options.autoplay) currentVideo.playVideo();
        }
        else if (!this.getPausedState(currentVideo, currentSlideType)) currentVideo.pauseVideo();
      });
    });
    observer.observe(this);
  }

  init(event = null, updateSwiper = false) {
    // only used for the Theme Editor to check if the swiper matches the section that was updated
    if (updateSwiper && event.detail.sectionId !== this.swiperOptions.sectionID) return;

    // Check if we have extra options on the HTML
    if (this.dataset.options) {
      const options = JSON.parse(this.dataset.options);
      if (options) {
        this.swiperOptions = {
          ...this.swiperOptions,
          ...options,
        };
      }
    }

    if (this.swiperOptions.destroyAfter && this.swiperInstance) {
      const ww = window.innerWidth;
      if (ww >= this.swiperOptions.destroyAfter) {
        this.swiperInstance.destroy();
        return;
      }
    }

    if (updateSwiper && this.swiperInstance) {
      this.updateSwiper();
    }
    else {
      // Call swiper with selected swiper element and options
      this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
    }

    if (this.swiperOptions.isPromoSlider) {
      this.lastColorScheme = null;
      const activeSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex];
      if (activeSlide.classList.contains('color-scheme')) {
        const colorScheme = this.getColorScheme(activeSlide);
        if (this.swiperInstance.pagination.el) {
          this.swiperInstance.pagination.el.classList.add('color-scheme', colorScheme);
          this.lastColorScheme = colorScheme;
        }
      }

      this.swiperInstance.on('slideChangeTransitionStart', swiper => {
        if (this.lastColorScheme !== null) {
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide.classList.contains('color-scheme')) {
            const colorScheme = this.getColorScheme(activeSlide);

            // If the color scheme is different from the last one, remove bg to prevent color changing before slide has received new color scheme
            if (colorScheme !== this.lastColorScheme) {
              swiper.pagination.el.style.backgroundColor = 'transparent';
            }
          }
        }
      });

      this.swiperInstance.on('transitionEnd', swiper => {
        if (this.lastColorScheme !== null) {
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide.classList.contains('color-scheme')) {
            this.setColorScheme(activeSlide);
          }
        }
      });
    }
  }

  setColorScheme(slide, updateClasses = true, updateBg = true) {
    if (updateBg) {
      this.swiperInstance.pagination.el.style.backgroundColor = '';
    }

    if (slide) {
      const colorScheme = this.getColorScheme(slide);

      if (updateClasses) {
        this.swiperInstance.pagination.el.classList.remove(this.lastColorScheme);
        this.swiperInstance.pagination.el.classList.add(colorScheme);
        this.lastColorScheme = colorScheme;
      }
    }
  }

  getColorScheme(slide) {
    return slide.classList.contains('color-scheme--primary') ? 'color-scheme--primary' : slide.classList.contains('color-scheme--secondary') ? 'color-scheme--secondary' : slide.classList.contains('color-scheme--accent') ? 'color-scheme--accent' : slide.classList.contains('color-scheme--accent-2') ? 'color-scheme--accent-2' : slide.classList.contains('color-scheme--accent-3') ? 'color-scheme--accent-3' : slide.classList.contains('color-scheme--accent-4') ? 'color-scheme--accent-4' : slide.classList.contains('color-scheme--white') ? 'color-scheme--white' : slide.classList.contains('color-scheme--black') ? 'color-scheme--black' : 'color-scheme--gray';
  }

  updateSwiper() {
    this.swiperInstance.update();

    if (this.swiperOptions.navigation?.nextEl) {
      this.swiperInstance.navigation.init();
      this.swiperInstance.navigation.update();
    }
    if (this.swiperOptions.pagination?.el) {
      this.swiperInstance.pagination.init();
      this.swiperInstance.pagination.render();
    }
    // SCROLLBAR doesn't update in the theme editor. This seems to be a bug in swiper.
    if (this.swiperOptions.scrollbar?.el) {
      this.swiperInstance.scrollbar.init();
      this.swiperInstance.scrollbar.updateSize();
      this.swiperInstance.scrollbar.setTranslate();
    }
  }

  /**
   * Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect(event) {
    if (event.detail.sectionId !== this.swiperOptions.sectionID) return;

    // Check if the slide index is set
    if (!('swiperSlideIndex' in event.target.dataset)) return;

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
    if (this.swiperOptions.loop) swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;

    // Slide to slide based on the data attribute from the target
    const sliderToUpdate = event.target.closest('[data-swiper]');
    sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
  }
  /*
    * Create fancybox
    */
  fancybox() {
    // Check if Fancybox exists
    if (!Fancybox) return false;

    Fancybox.bind('[data-fancybox]', {
      dragToClose: false,
      showClass: 'f-fadeIn',
      hideClass: 'f-fadeOut',
      mainClass: 'fancybox-products',
      Carousel: {
        transition: 'slide',
      },
      // Disable image zoom animation on opening and closing
      Images: {
        zoom: false,
      },
      Thumbs: {
        type: 'classic',
      },
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    });
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
